import { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TablePagination,
} from "@mui/material";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { useQuery } from "@tanstack/react-query";
import { fetchAdminBusinesses } from "../../api/admin";
import AdminBusinessDetailDrawer from "./AdminBusinessDetailDrawer";

function statusChip(status: string | null) {
  if (!status) return <Chip label="No plan" size="small" sx={{ backgroundColor: "#F1F5F9", color: "#94A3B8", fontSize: "0.72rem" }} />;
  const map: Record<string, { bg: string; color: string }> = {
    active:   { bg: "#F0FDF4", color: "#16A34A" },
    trialing: { bg: "#EFF6FF", color: "#3B82F6" },
    manual:   { bg: "#FFFBEB", color: "#B45309" },
    past_due: { bg: "#FEF3C7", color: "#D97706" },
    canceled: { bg: "#FEF2F2", color: "#DC2626" },
    unpaid:   { bg: "#FEF2F2", color: "#DC2626" },
  };
  const s = map[status] ?? { bg: "#F1F5F9", color: "#64748B" };
  return <Chip label={status} size="small" sx={{ backgroundColor: s.bg, color: s.color, fontSize: "0.72rem", fontWeight: 600 }} />;
}

export default function AdminBusinessesPage() {
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "businesses", page, pageSize],
    queryFn: () => fetchAdminBusinesses(page + 1, pageSize),
  });

  const businesses = data?.businesses ?? [];
  const total = data?.total ?? 0;

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}><CircularProgress size={28} sx={{ color: "#C9734F" }} /></Box>;
  if (isError) return <Alert severity="error">Failed to load businesses.</Alert>;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <BusinessRoundedIcon sx={{ color: "#C9734F", fontSize: 22 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0F172A" }}>Businesses</Typography>
        <Chip label={`${total} total`} size="small" sx={{ ml: 1, backgroundColor: "#F1F5F9", color: "#64748B" }} />
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: "12px" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", color: "#64748B", borderBottom: "1px solid #E2E8F0" }}>Business</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", color: "#64748B", borderBottom: "1px solid #E2E8F0" }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", color: "#64748B", borderBottom: "1px solid #E2E8F0" }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.78rem", color: "#64748B", borderBottom: "1px solid #E2E8F0" }}>Status</TableCell>
              <TableCell sx={{ borderBottom: "1px solid #E2E8F0", width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {businesses.map((biz) => (
              <TableRow
                key={biz.business_id}
                hover
                sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                onClick={() => setSelectedBusinessId(biz.business_id)}
              >
                <TableCell sx={{ fontSize: "0.84rem", fontWeight: 500, color: "#0F172A", py: 1.5 }}>{biz.name}</TableCell>
                <TableCell sx={{ fontSize: "0.82rem", color: "#64748B" }}>{biz.email ?? "—"}</TableCell>
                <TableCell sx={{ fontSize: "0.82rem", color: "#0F172A" }}>{biz.plan_name ?? <span style={{ color: "#94A3B8" }}>None</span>}</TableCell>
                <TableCell>{statusChip(biz.subscription_status)}</TableCell>
                <TableCell>
                  <Tooltip title="Manage subscription">
                    <IconButton size="small" sx={{ color: "#94A3B8" }} onClick={(e) => { e.stopPropagation(); setSelectedBusinessId(biz.business_id); }}>
                      <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {businesses.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 4, color: "#94A3B8", fontSize: "0.84rem" }}>No businesses found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onPageChange={(_, p) => setPage(p)}
          sx={{ borderTop: "1px solid #E2E8F0", fontSize: "0.78rem" }}
        />
      </TableContainer>

      <AdminBusinessDetailDrawer
        businessId={selectedBusinessId}
        onClose={() => setSelectedBusinessId(null)}
      />
    </Box>
  );
}
